import { useId, useState, type ReactNode } from 'react';

/**
 * Charts, in plain SVG against the design tokens.
 *
 * No charting library: the whole set here is five small forms, and a
 * dependency would cost more in bundle and in fighting its defaults than it
 * saves. Every one of them is driven by real figures from the API — there are
 * no illustrative series anywhere in this file.
 *
 * The categorical order below is fixed and never cycled. It was validated for
 * colour-vision separation against the light surface before use; the design
 * system's slate is deliberately absent, because at that chroma it reads as
 * grey and stops being an identity.
 */
export const SERIES = ['#4f62f0', '#0e9c8c', '#c7762e', '#6e48f3', '#c24664'] as const;

/** Anything past the fifth series folds into one neutral rest-of-book slice. */
export const SERIES_REST = '#8b94ab';

export function seriesColour(index: number): string {
  return SERIES[index] ?? SERIES_REST;
}

/** A floating tooltip, positioned by the pointer rather than the mark. */
function useTooltip() {
  const [tip, setTip] = useState<{ x: number; y: number; content: ReactNode } | null>(null);
  const node = tip ? (
    <div
      style={{
        position: 'fixed',
        left: tip.x + 14,
        top: tip.y - 10,
        zIndex: 60,
        pointerEvents: 'none',
        background: 'var(--ink)',
        color: '#fff',
        padding: '7px 10px',
        borderRadius: 'var(--r-sm)',
        fontSize: 12,
        lineHeight: 1.45,
        boxShadow: 'var(--shadow-lg)',
        maxWidth: 260,
      }}
    >
      {tip.content}
    </div>
  ) : null;
  return { tip, setTip, node };
}

// ------------------------------------------------------------------ trend --

export interface TrendPoint {
  label: string;
  value: number;
  /** Pre-formatted for the tooltip, so the chart never formats money itself. */
  display: string;
}

/**
 * Change over time, as an area with a crosshair.
 *
 * One series, so no legend — the card title names it. Points are marked only
 * where the reader can act on them: the ends and the hovered one.
 */
export function TrendArea({
  points,
  colour = SERIES[0],
  height = 132,
}: {
  points: TrendPoint[];
  colour?: string;
  height?: number;
}) {
  const gradientId = useId();
  const { setTip, node } = useTooltip();
  const [active, setActive] = useState<number | null>(null);

  if (points.length < 2) {
    return <ChartEmpty>Not enough history yet to draw a trend.</ChartEmpty>;
  }

  const width = 560;
  const padX = 8;
  const padY = 14;
  const max = Math.max(...points.map((p) => p.value));
  const min = Math.min(0, ...points.map((p) => p.value));
  const span = max - min || 1;

  const x = (i: number) => padX + (i * (width - padX * 2)) / (points.length - 1);
  const y = (v: number) => height - padY - ((v - min) / span) * (height - padY * 2);

  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(p.value).toFixed(1)}`).join(' ');
  const area = `${line} L ${x(points.length - 1).toFixed(1)} ${height} L ${x(0).toFixed(1)} ${height} Z`;

  return (
    <div style={{ position: 'relative' }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height, display: 'block' }}
        role="img"
        aria-label={`Trend across ${points.length} periods, ending at ${points[points.length - 1]!.display}`}
        onMouseLeave={() => {
          setActive(null);
          setTip(null);
        }}
        onMouseMove={(event) => {
          const box = event.currentTarget.getBoundingClientRect();
          const ratio = (event.clientX - box.left) / box.width;
          const index = Math.max(
            0,
            Math.min(points.length - 1, Math.round(ratio * (points.length - 1))),
          );
          setActive(index);
          setTip({
            x: event.clientX,
            y: event.clientY,
            content: (
              <>
                <div style={{ opacity: 0.75 }}>{points[index]!.label}</div>
                <div style={{ fontWeight: 600 }}>{points[index]!.display}</div>
              </>
            ),
          });
        }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={colour} stopOpacity="0.22" />
            <stop offset="1" stopColor={colour} stopOpacity="0" />
          </linearGradient>
        </defs>

        <path d={area} fill={`url(#${gradientId})`} />
        <path
          d={line}
          fill="none"
          stroke={colour}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />

        {active !== null ? (
          <line
            x1={x(active)}
            x2={x(active)}
            y1={padY / 2}
            y2={height}
            stroke="var(--line)"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}

        {points.map((p, i) =>
          i === active || i === points.length - 1 ? (
            <circle
              key={p.label}
              cx={x(i)}
              cy={y(p.value)}
              r="4.5"
              fill={colour}
              stroke="var(--card)"
              strokeWidth="2"
            />
          ) : null,
        )}
      </svg>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        {points.map((p, i) =>
          i === 0 || i === points.length - 1 || i === Math.floor(points.length / 2) ? (
            <span key={p.label} className="cell-t2">
              {p.label}
            </span>
          ) : null,
        )}
      </div>
      {node}
    </div>
  );
}

// -------------------------------------------------------------------- bars --

export interface BarRow {
  key: string;
  name: string;
  value: number;
  display: string;
  /** Only set where the row's identity is also carried elsewhere, e.g. a donut. */
  colour?: string;
  meta?: string;
}

/**
 * Magnitude across named things.
 *
 * One hue by default: the rows are already identified by their labels, and
 * giving each a colour would imply a grouping that is not there.
 */
export function BarList({ rows, colour = SERIES[0] }: { rows: BarRow[]; colour?: string }) {
  const { setTip, node } = useTooltip();
  if (rows.length === 0) return <ChartEmpty>Nothing to show yet.</ChartEmpty>;

  const max = Math.max(...rows.map((r) => r.value)) || 1;

  return (
    <div style={{ display: 'grid', gap: 13 }}>
      {rows.map((row) => (
        <div
          key={row.key}
          onMouseMove={(event) =>
            setTip({
              x: event.clientX,
              y: event.clientY,
              content: (
                <>
                  <div style={{ fontWeight: 600 }}>{row.name}</div>
                  <div>{row.display}</div>
                  {row.meta ? <div style={{ opacity: 0.75 }}>{row.meta}</div> : null}
                </>
              ),
            })
          }
          onMouseLeave={() => setTip(null)}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 5 }}>
            <span className="cell-t1" style={{ flex: 1 }}>
              {row.colour ? <Dot colour={row.colour} /> : null}
              {row.name}
            </span>
            <span className="cell-t1 num">{row.display}</span>
          </div>
          <div
            style={{
              height: 8,
              borderRadius: 4,
              background: 'var(--line-2)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${Math.max(2, (row.value / max) * 100)}%`,
                height: '100%',
                borderRadius: 4,
                background: row.colour ?? colour,
                transition: 'width .5s cubic-bezier(.22,.61,.36,1)',
              }}
            />
          </div>
        </div>
      ))}
      {node}
    </div>
  );
}

// ------------------------------------------------------------------- donut --

export interface Slice {
  key: string;
  name: string;
  value: number;
  display: string;
  colour: string;
}

/**
 * Composition of a whole.
 *
 * Capped at five named slices plus a rest — past that the segments are too
 * thin to compare and the legend stops being readable. Segments carry a 2px
 * surface gap so adjacent fills never touch.
 */
export function Donut({
  slices,
  centreValue,
  centreLabel,
}: {
  slices: Slice[];
  centreValue: string;
  centreLabel: string;
}) {
  const { setTip, node } = useTooltip();
  const [active, setActive] = useState<string | null>(null);

  const total = slices.reduce((sum, s) => sum + s.value, 0);
  if (total <= 0) return <ChartEmpty>Nothing financed yet.</ChartEmpty>;

  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: 132, height: 132, flexShrink: 0 }}>
        <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
          {slices.map((slice) => {
            const fraction = slice.value / total;
            // A 2px gap in circumference terms, so segments never abut.
            const gap = 2;
            const length = Math.max(0, fraction * circumference - gap);
            const dash = `${length.toFixed(2)} ${(circumference - length).toFixed(2)}`;
            const thisOffset = -offset * circumference;
            offset += fraction;

            return (
              <circle
                key={slice.key}
                cx="60"
                cy="60"
                r={radius}
                fill="none"
                stroke={slice.colour}
                strokeWidth={active === slice.key ? 19 : 16}
                strokeDasharray={dash}
                strokeDashoffset={thisOffset.toFixed(2)}
                style={{ transition: 'stroke-width .15s' }}
                onMouseEnter={() => setActive(slice.key)}
                onMouseMove={(event) =>
                  setTip({
                    x: event.clientX,
                    y: event.clientY,
                    content: (
                      <>
                        <div style={{ fontWeight: 600 }}>{slice.name}</div>
                        <div>{slice.display}</div>
                        <div style={{ opacity: 0.75 }}>{Math.round(fraction * 100)}% of the book</div>
                      </>
                    ),
                  })
                }
                onMouseLeave={() => {
                  setActive(null);
                  setTip(null);
                }}
              />
            );
          })}
        </svg>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeContent: 'center',
            textAlign: 'center',
          }}
        >
          <div className="num" style={{ fontSize: 17, fontWeight: 700, color: 'var(--ink)' }}>
            {centreValue}
          </div>
          <div className="cell-t2">{centreLabel}</div>
        </div>
      </div>

      {/* Legend, always present: identity is never carried by colour alone. */}
      <div style={{ display: 'grid', gap: 7, flex: 1, minWidth: 170 }}>
        {slices.map((slice) => (
          <div key={slice.key} style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <Dot colour={slice.colour} />
            <span className="cell-t1" style={{ flex: 1 }}>
              {slice.name}
            </span>
            <span className="cell-t2 num">{slice.display}</span>
          </div>
        ))}
      </div>
      {node}
    </div>
  );
}

// -------------------------------------------------------------------- ring --

/** One rate, said once. A gauge rather than a chart, so no legend and no axis. */
export function Ring({
  percent,
  caption,
  colour = SERIES[0],
}: {
  percent: number;
  caption: string;
  colour?: string;
}) {
  const clamped = Math.max(0, Math.min(100, percent));
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - clamped / 100);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: 132, height: 132, flexShrink: 0 }}>
        <svg viewBox="0 0 120 120" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
          <circle cx="60" cy="60" r={radius} fill="none" stroke="var(--line-2)" strokeWidth="14" />
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={colour}
            strokeWidth="14"
            strokeLinecap="round"
            strokeDasharray={circumference.toFixed(2)}
            strokeDashoffset={offset.toFixed(2)}
            style={{ transition: 'stroke-dashoffset .7s cubic-bezier(.22,.61,.36,1)' }}
          />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeContent: 'center' }}>
          <div className="num" style={{ fontSize: 23, fontWeight: 700, color: 'var(--ink)' }}>
            {Math.round(clamped)}%
          </div>
        </div>
      </div>
      <p className="cell-t2" style={{ flex: 1, minWidth: 170, margin: 0, lineHeight: 1.6 }}>
        {caption}
      </p>
    </div>
  );
}

// ------------------------------------------------------------------ funnel --

export interface FunnelStage {
  key: string;
  name: string;
  count: number;
}

/**
 * How many deals reached each stage. Counts, so currencies never mix.
 *
 * One hue, deepening along the rail. The stages are a single measure at
 * successive points, not five different things — giving each its own colour
 * would imply an identity that is not there, and would make the reader look
 * for a meaning in the difference between blue and amber.
 */
export function Funnel({
  stages,
  colour = SERIES[0],
  /**
   * Only meaningful where each stage is a subset of the one above it. On
   * disjoint buckets the share against the largest is a number with nothing
   * behind it, so the caller turns it off.
   */
  showShare = true,
}: {
  stages: FunnelStage[];
  colour?: string;
  showShare?: boolean;
}) {
  const max = Math.max(...stages.map((s) => s.count), 1);
  const shade = (index: number) =>
    stages.length < 2 ? 1 : 0.55 + (index / (stages.length - 1)) * 0.45;

  return (
    <div style={{ display: 'grid', gap: 11 }}>
      {stages.map((stage, index) => (
        <div key={stage.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className="cell-t1" style={{ width: 148, flexShrink: 0 }}>
            {stage.name}
          </span>
          <span
            style={{
              flex: 1,
              height: 22,
              borderRadius: 5,
              background: 'var(--line-2)',
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                display: 'block',
                width: `${Math.max(3, (stage.count / max) * 100)}%`,
                height: '100%',
                borderRadius: 5,
                background: colour,
                opacity: shade(index),
                transition: 'width .6s cubic-bezier(.22,.61,.36,1)',
              }}
            />
          </span>
          <span className="cell-t1 num" style={{ width: 34, textAlign: 'right' }}>
            {stage.count}
          </span>
          {showShare ? (
            <span className="cell-t2 num" style={{ width: 44, textAlign: 'right' }}>
              {Math.round((stage.count / max) * 100)}%
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

// ------------------------------------------------------------------ pieces --

export function Dot({ colour }: { colour: string }) {
  return (
    <i
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: 3,
        background: colour,
        marginRight: 7,
        verticalAlign: 'middle',
      }}
    />
  );
}

function ChartEmpty({ children }: { children: ReactNode }) {
  return (
    <div className="cell-t2" style={{ padding: '18px 0' }}>
      {children}
    </div>
  );
}
